// #region Imports

import chroma from 'chroma-js';
import type { CssColor } from '../../Utils/CssColor';
import type { ThemePalette } from '../../Utils/ThemePalette';
import type { ThemeGeneratorFactoryFn } from '../interfaces/IThemeGeneratorFactory';
import { LchThemeGeneratorStrategy } from './abstracts/LchThemeGeneratorStrategy';

// #endregion

/**
 * Cosmopolitan theme generator strategy.
 *
 * @remarks
 * Extends {@link LchThemeGeneratorStrategy} with a muted, desaturated aesthetic
 * that defines the Cosmopolitan visual language. Overrides only where the default
 * LCH behavior needs adjustment; all other behavior is inherited.
 *
 * @public
 */
export class CosmopolitanThemeGeneratorStrategy extends LchThemeGeneratorStrategy {

    // #region Fields

    private static readonly _CHROMA_REDUCTION_FACTOR = 0.9;

    // #endregion

    // #region Methods

    /**
     * Adapts a color to the target mode by inverting its perceptual lightness (LCH L*)
     * with a slight chroma reduction for the Cosmopolitan's muted aesthetic.
     *
     * @override
     * @public
     * @param color - The color to adapt
     * @returns The adapted color with inverted lightness and reduced chroma
     */
    public override adaptColorToMode(color: CssColor): CssColor {
        const c = chroma(color);
        const [l, chromaVal, h] = c.lch();
        const invertedL = Math.min(Math.max(100 - l, 5), 95);
        const reducedChroma = chromaVal * CosmopolitanThemeGeneratorStrategy._CHROMA_REDUCTION_FACTOR;

        return chroma.lch(invertedL, reducedChroma, isNaN(h) ? 0 : h).hex() as CssColor;
    }

    /**
     * Generates a color palette with Cosmopolitan-specific dark-mode mixing.
     *
     * @remarks
     * Light mode uses the standard LCH base class implementation.
     * Dark mode uses a simplified three-anchor approach (steps 0, 100, 500)
     * to produce the flat, muted dark palette that defines Cosmopolitan.
     *
     * @override
     * @public
     * @param baseColor - The base/accent color
     * @param mode - 'light' or 'dark' mode
     * @returns The generated theme palette with shade steps 0–900
     */
    public override generatePalette(baseColor: CssColor, mode: 'dark' | 'light'): Required<ThemePalette> {
        if (mode === 'light') {
            return super.generatePalette(baseColor, mode);
        }

        const baseDark = chroma('#000000');
        const palette: Record<string, CssColor> = {};
        const steps = [0, 100, 500];
        const amounts = [0, 30, 100];

        steps.forEach((step, i) => {
            palette[`${step}`] = chroma.mix(baseDark, baseColor, amounts[i] / 100, 'rgb').hex() as CssColor;
        });

        return {
            "0": palette['0'],
            "50": palette['0'],
            "100": palette['100'],
            "200": palette['100'],
            "300": palette['100'],
            "400": palette['100'],
            "500": palette['500'],
            "600": palette['500'],
            "700": palette['500'],
            "800": palette['500'],
            "900": palette['500']
        };
    }

    // #endregion

}

/**
 * Generates a Cosmopolitan theme generator strategy.
 *
 * @public
 */
export function createCosmopolitanTheme(): ThemeGeneratorFactoryFn {
    const strategy = new CosmopolitanThemeGeneratorStrategy();

    return () => strategy;
}
